import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  TrendingUp, Target, AlertTriangle, Activity, Clock, Snowflake, Zap,
  HelpCircle, Briefcase, Users, ExternalLink, ChevronRight, PieChart as PieIcon,
  Building2, Trophy, XCircle, Info, ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import MetricInfo, { METRIC_INFO_TEXTS } from './analytics/MetricInfo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  ok:       { dot: 'bg-emerald-500', ring: 'hover:ring-emerald-300', text: 'text-emerald-700', label: 'OK' },
  warning:  { dot: 'bg-amber-500',   ring: 'hover:ring-amber-300',   text: 'text-amber-700',   label: 'Atención' },
  critical: { dot: 'bg-red-500',     ring: 'hover:ring-red-300',     text: 'text-red-700',     label: 'Crítico' },
};

const HEALTH_ICONS = {
  pipeline_open: Briefcase,
  success_rate: Trophy,
  forecast: Target,
  stagnation: Snowflake,
  overdue_closes: Clock,
  pipeline_health: Activity,
};

const METRIC_INFO_KEY = {
  pipeline_open: 'pipeline_live',
  success_rate: 'success_rate',
  forecast: 'forecast_weighted',
  stagnation: 'stagnation',
  overdue_closes: 'overdue_closes',
  pipeline_health: 'pipeline_health',
};

const eur = (n) => {
  if (n === null || n === undefined) return '—';
  if (n === 0) return '0 €';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M€`;
  if (n >= 1_000) return `${Math.round(n / 1_000)} k€`;
  return `${Math.round(n)} €`;
};

const TooltipBadge = ({ label, tooltipText }) => (
  <div className="inline-flex items-center gap-0.5">
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">{label}</Badge>
    {tooltipText && <MetricInfo text={tooltipText} testId={`info-badge-${label.toLowerCase()}`} ariaLabel={label} />}
  </div>
);

// ─── Health card ─────────────────────────────────────────────────────────────
const HealthCard = ({ item, onOpen }) => {
  const style = item.status ? (STATUS_STYLES[item.status] || null) : null;
  const Icon = HEALTH_ICONS[item.key] || Activity;
  const infoText = METRIC_INFO_TEXTS[METRIC_INFO_KEY[item.key]];
  const isForecastNoData = item.key === 'forecast' && item.available === false;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`text-left w-full bg-card border rounded-lg p-4 transition-all hover:shadow-md ring-1 ring-transparent ${style?.ring || 'hover:ring-slate-300'} group ${isForecastNoData ? 'opacity-70' : ''}`}
      data-testid={`health-card-${item.key}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5">
          <Icon className={`w-4 h-4 ${style?.text || 'text-slate-600'}`} />
          <span className="text-xs font-medium text-muted-foreground">{item.label}</span>
          {infoText && <MetricInfo text={infoText} testId={`info-${item.key}`} ariaLabel={item.label} />}
        </div>
        <div className="flex items-center gap-1.5">
          <TooltipBadge label="Stock" tooltipText={METRIC_INFO_TEXTS.stock} />
          {style && <span className={`w-2 h-2 rounded-full ${style.dot}`} title={style.label} />}
        </div>
      </div>
      {/* Body per card type */}
      {item.key === 'pipeline_open' && (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold">{item.value}</span>
            <span className="text-xs text-muted-foreground">abiertas</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-600">
            Importe: <strong>{eur(item.amount_eur)}</strong>
            {item.no_amount_count > 0 && <span className="text-amber-700 ml-1">· {item.no_amount_count} sin importe</span>}
          </div>
        </>
      )}
      {item.key === 'success_rate' && (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold">{item.pct}%</span>
            <span className="text-xs text-muted-foreground">{item.value} / {item.total} cerradas</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-600">
            <span className="text-emerald-700">Ganadas {item.won}</span> ·
            <span className="text-red-700 ml-1">Perdidas {item.lost}</span> ·
            <span className="text-slate-600 ml-1">Descartadas {item.discarded}</span>
          </div>
        </>
      )}
      {item.key === 'forecast' && (
        <>
          {isForecastNoData ? (
            <>
              <div className="text-xs text-amber-700 font-medium leading-snug">{item.message}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">Rellena la probabilidad en cada opp para activarlo</div>
            </>
          ) : (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold">{eur(item.value_eur)}</span>
              </div>
              <div className="mt-1 text-[11px] text-slate-600">Basado en {item.with_probability} de {item.total_open} abiertas con probabilidad</div>
            </>
          )}
        </>
      )}
      {item.key === 'stagnation' && (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold">{item.value}</span>
            <span className="text-xs text-muted-foreground">de {item.total} &gt; 60 días</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-600">Mediana: <strong>{item.median_days}</strong> días sin cambio de etapa</div>
        </>
      )}
      {item.key === 'overdue_closes' && (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-red-700">{item.value}</span>
            <span className="text-xs text-muted-foreground">vencidas</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-600">
            Próximas 30d: <strong>{item.upcoming}</strong> · Sin fecha: <strong className="text-amber-700">{item.without_date}</strong>
          </div>
        </>
      )}
      {item.key === 'pipeline_health' && (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold">{item.pct}%</span>
            <span className="text-xs text-muted-foreground">medio</span>
          </div>
          <div className="mt-1 text-[10px] text-slate-600 space-y-0.5">
            <div>Importe: <strong>{item.pct_amount}%</strong> · Prob: <strong>{item.pct_probability}%</strong></div>
            <div>Fecha: <strong>{item.pct_close_date}%</strong> · Actividad: <strong>{item.pct_activity}%</strong></div>
          </div>
        </>
      )}
      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
        <span className={style?.text || ''}>{style?.label || '\u00A0'}</span>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Ver lista <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
};

// ─── Conversion Rates (cohort) ───────────────────────────────────────────────
const ConversionRates = ({ rates }) => (
  <div className="space-y-2" data-testid="conversion-rates">
    {rates.map(r => {
      const pct = r.rate === null ? null : r.rate;
      const color = pct === null ? '#94a3b8' : (pct >= 50 ? '#10b981' : (pct >= 25 ? '#f59e0b' : '#ef4444'));
      return (
        <div key={r.key} className="text-xs" data-testid={`rate-${r.key}`}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="font-medium flex items-center gap-1">
              {r.from} <ArrowRight className="w-3 h-3 inline" /> {r.to}
            </span>
            <span className="text-muted-foreground">
              {pct === null ? <span className="italic">sin cohorte</span> : <><strong style={{ color }}>{pct}%</strong> <span className="text-[10px]">· cohorte {r.cohort}</span></>}
            </span>
          </div>
          <div className="h-2 bg-muted/40 rounded overflow-hidden">
            <div className="h-full rounded" style={{ width: `${pct || 0}%`, backgroundColor: color }} />
          </div>
        </div>
      );
    })}
    <div className="text-[10px] text-muted-foreground pt-2 border-t mt-2">
      Cohortes reales: nº de oportunidades que alguna vez entraron en la etapa de origen. Máximo posible 100%.
    </div>
  </div>
);

// ─── Stagnation Buckets ──────────────────────────────────────────────────────
const StagnationBars = ({ buckets, onClick }) => {
  const max = Math.max(1, ...buckets.map(b => b.value));
  return (
    <div className="space-y-2" data-testid="stagnation-bars">
      {buckets.map(b => (
        <button
          key={b.key}
          type="button"
          onClick={() => b.value > 0 && onClick(b.key)}
          disabled={b.value === 0}
          className="w-full text-left group"
          data-testid={`stagn-${b.key}`}
        >
          <div className="flex items-center justify-between text-xs mb-0.5">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: b.color }} />
              <span className="font-medium group-hover:underline">{b.label}</span>
            </span>
            <span className="text-muted-foreground">{b.value} <span className="text-[10px]">({b.pct}%)</span></span>
          </div>
          <div className="h-4 bg-muted/40 rounded overflow-hidden">
            <div className="h-full rounded" style={{ width: `${(b.value / max) * 100}%`, backgroundColor: b.color }} />
          </div>
        </button>
      ))}
    </div>
  );
};

// ─── Close table (tabs: vencidos / próximos / sin fecha) ──────────────────────
const CloseTable = ({ rows, kind, onOpen }) => {
  if (!rows || rows.length === 0) {
    const empty = { overdue: 'No hay cierres vencidos', upcoming: 'No hay cierres próximos', no_date: 'Todas las abiertas tienen fecha de cierre' };
    return <div className="p-6 text-center text-sm text-muted-foreground">{empty[kind]}</div>;
  }
  return (
    <div className="overflow-x-auto" data-testid={`close-table-${kind}`}>
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground border-b bg-muted/30">
          <tr>
            <th className="text-left px-3 py-2">Oportunidad</th>
            <th className="text-left px-3 py-2">Etapa</th>
            <th className="text-left px-3 py-2">Responsable</th>
            <th className="text-right px-3 py-2">Importe</th>
            <th className="text-right px-3 py-2">{kind === 'overdue' ? 'Vencida hace' : kind === 'upcoming' ? 'En' : 'Fecha'}</th>
            <th className="text-right px-3 py-2 w-16">Acción</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`close-row-${r.id}`}>
              <td className="px-3 py-2 font-medium truncate max-w-xs">{r.title}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.stage}</td>
              <td className={`px-3 py-2 ${r.owner === 'Sin asignar' ? 'text-red-700 font-medium' : 'text-muted-foreground'}`}>{r.owner}</td>
              <td className="px-3 py-2 text-right">{r.amount_eur ? eur(r.amount_eur) : <span className="text-amber-700">Sin importe</span>}</td>
              <td className="px-3 py-2 text-right">
                {kind === 'overdue' && <span className="text-red-700 font-semibold">+{Math.abs(r.days_offset)}d</span>}
                {kind === 'upcoming' && <span className={r.days_offset <= 7 ? 'text-orange-700 font-semibold' : ''}>{r.days_offset}d</span>}
                {kind === 'no_date' && <span className="italic text-muted-foreground">sin fecha</span>}
              </td>
              <td className="px-3 py-2 text-right">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onOpen(r.id)} data-testid={`open-opp-${r.id}`} title="Abrir oportunidad">
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Definitions Panel ──────────────────────────────────────────────────────
const DefinitionsPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="link" size="sm" className="text-xs h-7 px-2" data-testid="definitions-link">
        <HelpCircle className="w-3.5 h-3.5 mr-1" />
        ¿Cómo se calculan estas métricas?
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="definitions-panel">
      <SheetHeader>
        <SheetTitle>Definiciones — Analítica de Oportunidades</SheetTitle>
      </SheetHeader>
      <div className="mt-4 space-y-4 text-sm">
        {[
          ['Pipeline vivo', METRIC_INFO_TEXTS.pipeline_live],
          ['Tasa de éxito', METRIC_INFO_TEXTS.success_rate],
          ['Forecast ponderado', METRIC_INFO_TEXTS.forecast_weighted],
          ['Estancamiento', METRIC_INFO_TEXTS.stagnation],
          ['Cierres vencidos', METRIC_INFO_TEXTS.overdue_closes],
          ['Salud del pipeline', METRIC_INFO_TEXTS.pipeline_health],
          ['Sin actividad', METRIC_INFO_TEXTS.opp_no_activity],
          ['Motivos de descarte', METRIC_INFO_TEXTS.discard_motives],
          ['Conversión entre etapas', METRIC_INFO_TEXTS.conversion_cohort],
          ['Por rango de valor', METRIC_INFO_TEXTS.value_range],
          ['Por probabilidad', METRIC_INFO_TEXTS.probability_dist],
          ['Por tipo de empresa', METRIC_INFO_TEXTS.by_ctype_open],
          ['Pipeline por responsable', METRIC_INFO_TEXTS.by_owner_open],
          ['Análisis competitivo', METRIC_INFO_TEXTS.competitive],
          ['Ciclo de venta', METRIC_INFO_TEXTS.cycle_time],
          ['Etiqueta Stock', METRIC_INFO_TEXTS.stock],
        ].map(([title, text]) => (
          <div key={title}>
            <div className="font-semibold text-sm mb-0.5">{title}</div>
            <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

// ─── Main component ─────────────────────────────────────────────────────────
const OpportunityAnalyticsV2 = ({ clientTypeFilter, ownerFilter, periodFilter }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionTab, setActionTab] = useState('overdue');

  useEffect(() => {
    fetchAnalytics();
  }, [clientTypeFilter, ownerFilter, periodFilter]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const params = new URLSearchParams();
      // Map Pipeline filters to backend params
      params.append('period', periodFilter || 'all');
      if (ownerFilter === 'me') {
        params.append('owner_mode', 'me');
      } else if (ownerFilter === 'unassigned') {
        params.append('owner_mode', 'unassigned');
      } else if (ownerFilter && ownerFilter !== 'all') {
        params.append('owner_mode', 'specific');
        params.append('owner', ownerFilter);
      } else {
        params.append('owner_mode', 'all');
      }
      if (clientTypeFilter && clientTypeFilter.length && clientTypeFilter.length < 3) {
        params.append('company_types', clientTypeFilter.join(','));
      }
      const { data } = await axios.get(`${API}/analytics/opportunities/v2?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching opps analytics v2:', err);
    } finally {
      setLoading(false);
    }
  };

  const openOpp = (id) => {
    // Trigger Pipeline detail via URL param (Pipeline.js can react to opportunityId)
    window.location.href = `/pipeline?opportunityId=${id}`;
  };

  if (loading && !analytics) return <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">Cargando analíticas…</div>;
  if (!analytics) return <div className="text-center py-12 text-muted-foreground">No hay datos disponibles</div>;

  const {
    summary, health, conversion_rates, stagnation_buckets, close_table, no_activity,
    by_value_range, by_probability, by_company_type, by_owner, discard_reasons,
    discard_unclassified, competitive, pending_awards, coverage_notes, cycle,
  } = analytics;

  return (
    <div className="space-y-6" data-testid="opportunity-analytics-v2">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Target className="w-5 h-5" /> Analítica de Oportunidades
          </h2>
          <p className="text-xs text-muted-foreground">
            <strong>{summary.total}</strong> oportunidades ·
            <span className="mx-1">{summary.open} abiertas <strong>{eur(summary.open_amount_eur)}</strong></span>·
            <span className="mx-1">{summary.won} ganadas · {summary.lost} perdidas · {summary.discarded} descartadas</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DefinitionsPanel />
          <Badge variant="outline" className="text-[10px]" data-testid="filters-inherited-tag">
            Filtros heredados de la cabecera: periodo <strong className="mx-1">{periodFilter || 'all'}</strong> · responsable <strong>{ownerFilter || 'all'}</strong>
          </Badge>
        </div>
      </div>

      {/* Coverage notes */}
      {coverage_notes && coverage_notes.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="coverage-notes">
          <Info className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <div className="text-xs font-semibold text-amber-900">Aviso de cobertura de dato</div>
            {coverage_notes.map((n, i) => (
              <div key={i} className="text-xs text-amber-800 mt-0.5">{n.text}</div>
            ))}
          </div>
        </div>
      )}

      {/* BLOCK 1: 6 health cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="block-health">
        {Object.values(health).map(h => (
          <HealthCard key={h.key} item={h} onOpen={() => { /* nav to filtered pipeline */ window.location.href = h.filter_url || '/pipeline'; }} />
        ))}
      </div>

      {/* BLOCK 2: charts */}
      <div className="grid md:grid-cols-2 gap-5" data-testid="block-charts">
        <Card data-testid="chart-conversion">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Conversión entre etapas (por cohorte)
              <MetricInfo text={METRIC_INFO_TEXTS.conversion_cohort} testId="info-conversion" ariaLabel="Conversión" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ConversionRates rates={conversion_rates} />
          </CardContent>
        </Card>

        <Card data-testid="chart-stagnation">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Estancamiento (días sin cambio de etapa)
              <MetricInfo text={METRIC_INFO_TEXTS.stagnation} testId="info-stagnation-chart" ariaLabel="Estancamiento" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StagnationBars buckets={stagnation_buckets} onClick={(k) => { window.location.href = `/pipeline?open=true&stagnation_bucket=${k}`; }} />
          </CardContent>
        </Card>

        <Card data-testid="chart-value-range">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Por rango de valor
              <MetricInfo text={METRIC_INFO_TEXTS.value_range} testId="info-value-range" ariaLabel="Rango de valor" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {by_value_range.map(r => (
                <div key={r.key} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${r.is_data_quality ? 'bg-amber-50 border border-amber-200' : 'bg-muted/30'}`} data-testid={`vr-${r.key}`}>
                  <div className="flex items-center gap-2">
                    {r.is_data_quality && <AlertTriangle className="w-3 h-3 text-amber-700" />}
                    <span className={`font-medium ${r.is_data_quality ? 'text-amber-900' : ''}`}>{r.label}</span>
                  </div>
                  <div className="text-muted-foreground text-right">
                    <span className="font-semibold text-foreground">{r.count}</span>
                    <span className="text-[10px] ml-1">· abiertas {r.open_count}</span>
                    {r.value_eur !== null && <span className="text-[10px] ml-1">· {eur(r.open_value_eur)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="chart-probability">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Por probabilidad (pipeline vivo)
              <MetricInfo text={METRIC_INFO_TEXTS.probability_dist} testId="info-probability" ariaLabel="Probabilidad" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {by_probability.valid.map(p => {
                const c = { Alta: '#10b981', Media: '#eab308', Baja: '#ef4444' }[p.key];
                return (
                  <div key={p.key} className="p-2 rounded border-l-4" style={{ borderColor: c, backgroundColor: `${c}10` }} data-testid={`prob-${p.key}`}>
                    <div className="text-xs font-medium">{p.label}</div>
                    <div className="text-lg font-bold">{p.count}</div>
                    <div className="text-[10px] text-muted-foreground">{eur(p.value_eur)}</div>
                  </div>
                );
              })}
            </div>
            {by_probability.undefined.count > 0 && (
              <div className="p-2 rounded bg-amber-50 border border-amber-200" data-testid="prob-undefined">
                <div className="flex items-center gap-1.5 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
                  <span className="font-semibold text-amber-900">Sin definir: {by_probability.undefined.count} opps ({by_probability.undefined.pct}%)</span>
                </div>
                <div className="text-[11px] text-amber-800 mt-0.5">
                  Aviso de dato incompleto. Estas oportunidades no pueden entrar en el forecast.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="chart-ctype">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Pipeline vivo por tipo de empresa
              <MetricInfo text={METRIC_INFO_TEXTS.by_ctype_open} testId="info-ctype" ariaLabel="Tipo empresa" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={by_company_type.filter(c => c.count > 0)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={(v, n, p) => n === 'count' ? [`${v} opps`, 'Cantidad'] : [eur(v), 'Importe']} />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="chart-owner">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Pipeline vivo por responsable
              <MetricInfo text={METRIC_INFO_TEXTS.by_owner_open} testId="info-owner" ariaLabel="Responsable" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[260px] overflow-y-auto space-y-2 pr-1">
              {by_owner.slice(0, 12).map(o => {
                const max = Math.max(1, ...by_owner.map(x => x.open_value_eur));
                const width = (o.open_value_eur / max) * 100;
                return (
                  <div key={o.key} data-testid={`owner-${o.key}`}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className={`font-medium truncate max-w-[180px] ${o.key === '__unassigned__' ? 'text-red-700' : ''}`}>{o.label}</span>
                      <span className="text-muted-foreground">
                        <strong className="text-foreground">{eur(o.open_value_eur)}</strong> <span className="text-[10px]">· {o.open_count} opps</span>
                      </span>
                    </div>
                    <div className="h-3 bg-muted/40 rounded overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${width}%` }} />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Ganadas: <strong className="text-emerald-700">{o.won_count}</strong> ({eur(o.won_value_eur)}) · Perdidas: <strong className="text-red-700">{o.lost_count}</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="chart-discard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Motivos de descarte y pérdida
              <MetricInfo text={METRIC_INFO_TEXTS.discard_motives} testId="info-discard" ariaLabel="Motivos" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Feb 2026 (PR-B): warning when there are unclassified discards. */}
            {discard_unclassified && (discard_unclassified.discard_count > 0 || discard_unclassified.lost_count > 0) && (
              <div
                className="mb-3 p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-900"
                data-testid="discard-unclassified-warning"
              >
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                {discard_unclassified.discard_count > 0 && (
                  <span>{discard_unclassified.discard_count} descarte{discard_unclassified.discard_count !== 1 ? 's' : ''} sin clasificar. </span>
                )}
                {discard_unclassified.lost_count > 0 && (
                  <span>{discard_unclassified.lost_count} pérdida{discard_unclassified.lost_count !== 1 ? 's' : ''} sin clasificar. </span>
                )}
                Clasifícalos desde la ficha para poder analizar tendencias.
              </div>
            )}
            {discard_reasons.every(r => (r.discard_count || r.lost_count || 0) === 0) ? (
              <div className="text-xs text-muted-foreground text-center py-6">Ninguna oportunidad descartada o perdida en el rango seleccionado</div>
            ) : (
              <div className="space-y-1.5">
                {discard_reasons.filter(r => (r.discard_count || 0) + (r.lost_count || 0) > 0).map(r => {
                  const max = Math.max(1, ...discard_reasons.map(x => (x.discard_count || 0) + (x.lost_count || 0)));
                  const total = (r.discard_count || 0) + (r.lost_count || 0);
                  return (
                    <div key={r.key} data-testid={`discard-${r.key}`}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-medium">{r.label}</span>
                        <span className="text-muted-foreground font-semibold whitespace-nowrap">
                          <span className="text-slate-700">{r.discard_count || 0}</span>
                          <span className="text-muted-foreground/70"> descartes · </span>
                          <span className="text-red-700">{r.lost_count || 0}</span>
                          <span className="text-muted-foreground/70"> perdidas</span>
                        </span>
                      </div>
                      <div className="h-3 bg-muted/40 rounded overflow-hidden flex">
                        <div className="h-full bg-slate-500" style={{ width: `${((r.discard_count || 0) / max) * 100}%` }} />
                        <div className="h-full bg-red-500" style={{ width: `${((r.lost_count || 0) / max) * 100}%` }} />
                      </div>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-wide">Total: {total}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="chart-competitive">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Análisis competitivo
              <MetricInfo text={METRIC_INFO_TEXTS.competitive} testId="info-competitive" ariaLabel="Competitivo" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!competitive.ready ? (
              <div className="p-3 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="competitive-not-ready">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                Sin masa crítica: solo {competitive.with_data} oportunidades públicas cerradas. Umbral: {competitive.threshold}.
              </div>
            ) : (
              <div className="space-y-4">
                {/* BLOQUE A: adjudicado a TDS */}
                <div data-testid="competitive-tds-block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Adjudicado a TDS</div>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div><div className="text-muted-foreground">Expedientes</div><div className="text-lg font-bold text-emerald-700">{competitive.awarded_to_tds?.count ?? 0}</div></div>
                    <div><div className="text-muted-foreground">Baja media propia</div><div className="text-lg font-bold">{competitive.awarded_to_tds?.avg_own_discount != null ? `${competitive.awarded_to_tds.avg_own_discount}%` : '—'}</div></div>
                    <div><div className="text-muted-foreground">Importe adjudicado</div><div className="text-lg font-bold text-emerald-700">{eur(competitive.awarded_to_tds?.total_award_eur || 0)}</div></div>
                  </div>
                </div>
                {/* BLOQUE B: adjudicado a terceros */}
                <div data-testid="competitive-third-block" className="border-t pt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Adjudicado a terceros</span>
                    <span className="text-[10px] text-muted-foreground">
                      Baja media adjudicatario: {competitive.awarded_to_third_party?.avg_awardee_discount != null ? `${competitive.awarded_to_third_party.avg_awardee_discount}%` : '—'}
                    </span>
                  </div>
                  {(competitive.awarded_to_third_party?.ranking || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic py-2">Sin competidores registrados en el rango</div>
                  ) : (
                    <div className="space-y-1">
                      {(competitive.awarded_to_third_party?.ranking || []).slice(0, 5).map((a) => (
                        <div key={a.awardee} className="flex items-center justify-between text-xs">
                          <span className="truncate">{a.awardee}</span>
                          <span className="whitespace-nowrap">
                            <Badge variant="outline" className="text-[10px] mr-1">{a.count}</Badge>
                            <span className="text-red-700">{eur(a.total_amount_eur)}</span>
                            {a.avg_discount != null && <span className="text-muted-foreground ml-1">· {a.avg_discount}%</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Cobertura */}
                {competitive.coverage?.total_exigible > 0 && (
                  <div className="border-t pt-2 text-[11px] text-muted-foreground italic" data-testid="competitive-coverage">
                    {competitive.coverage.message}
                  </div>
                )}
                {/* Diferencial de baja (solo pública) */}
                <div className="border-t pt-3" data-testid="competitive-diff-baja">
                  <div className="flex items-baseline gap-1.5 mb-0.5">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Diferencial de baja</span>
                    <MetricInfo
                      text="Cuánto más barato que nosotros ofertó quien ganó. Si es positivo y alto, perdemos por precio; si es cercano a cero y perdemos, el problema es técnico."
                      testId="info-diff-baja"
                      ariaLabel="Diferencial de baja"
                    />
                  </div>
                  {competitive.diff_baja?.available ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold">{competitive.diff_baja.avg_diff_pct}%</span>
                      <span className="text-[10px] text-muted-foreground">
                        muestra {competitive.diff_baja.sample} de {competitive.diff_baja.total_lost_public} perdidas públicas
                      </span>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">Sin muestra suficiente</div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Adjudicaciones pendientes de consultar (Feb 2026 B4.4) */}
        {pending_awards && (
          <Card data-testid="chart-pending-awards">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                Adjudicaciones pendientes de consultar
                <MetricInfo
                  text="Solo licitaciones públicas y oportunidades privadas/TYPSA con seguimiento activado, con fecha prevista de resolución pasada y sin adjudicatario registrado."
                  testId="info-pending-awards"
                  ariaLabel="Adjudicaciones pendientes"
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pending_awards.count === 0 ? (
                <div className="text-xs text-muted-foreground py-2">Sin pendientes</div>
              ) : (
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-amber-700" data-testid="pending-awards-count">{pending_awards.count}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {(pending_awards.opps || []).slice(0, 5).map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => openOpp(o.id)}
                        className="block w-full text-left px-2 py-1 hover:bg-muted/40 rounded truncate"
                      >
                        · {o.title} <span className="text-muted-foreground/70">({o.expected_resolution_date})</span>
                      </button>
                    ))}
                    {pending_awards.count > 5 && (
                      <div className="text-[10px] text-muted-foreground italic mt-1">
                        + {pending_awards.count - 5} más
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cycle time note */}
      {!cycle.available && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center gap-2 text-xs text-slate-700" data-testid="cycle-not-available">
          <Clock className="w-4 h-4 text-slate-500" />
          <strong>Tiempo de ciclo:</strong> {cycle.message}
        </div>
      )}
      {cycle.available && (
        <div className="rounded-lg border p-3 flex items-center gap-2 text-xs" data-testid="cycle-available">
          <Clock className="w-4 h-4 text-emerald-600" />
          <strong>Ciclo de venta mediano:</strong> {cycle.median_days} días (muestra {cycle.sample} de {cycle.total_won} ganadas)
        </div>
      )}

      {/* BLOCK 3: Cierres vencidos y próximos + Sin actividad */}
      <Card data-testid="block-action">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Requieren acción
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={actionTab} onValueChange={setActionTab}>
            <TabsList className="mx-3 mt-3 flex-wrap h-auto" data-testid="action-tabs">
              <TabsTrigger value="overdue" data-testid="tab-overdue">Vencidas ({close_table.overdue.length})</TabsTrigger>
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">Próximas 30d ({close_table.upcoming.length})</TabsTrigger>
              <TabsTrigger value="no_date" data-testid="tab-no_date">Sin fecha ({close_table.no_date.length})</TabsTrigger>
              <TabsTrigger value="no_activity" data-testid="tab-no_activity">Sin actividad ({no_activity.open_without_activity})</TabsTrigger>
            </TabsList>
            <TabsContent value="overdue" className="mt-0"><CloseTable rows={close_table.overdue} kind="overdue" onOpen={openOpp} /></TabsContent>
            <TabsContent value="upcoming" className="mt-0"><CloseTable rows={close_table.upcoming} kind="upcoming" onOpen={openOpp} /></TabsContent>
            <TabsContent value="no_date" className="mt-0"><CloseTable rows={close_table.no_date} kind="no_date" onOpen={openOpp} /></TabsContent>
            <TabsContent value="no_activity" className="mt-0">
              <div className="p-3 text-xs text-muted-foreground border-b">
                <strong>{no_activity.open_without_activity}</strong> de {no_activity.total_open} oportunidades abiertas no tienen actividad registrada ({no_activity.pct_open}%).
                En el total (abiertas + cerradas): <strong>{no_activity.total_without_activity}</strong> de {no_activity.total}.
              </div>
              <div className="p-3">
                <Button size="sm" variant="outline" onClick={() => { window.location.href = '/pipeline?open=true&no_activity=true'; }} data-testid="open-no-activity-list">
                  Ver todas en la lista
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default OpportunityAnalyticsV2;
